import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Icono } from '@xiltrion/ui/icono';
import { Button } from '@xiltrion/ui/button';
import { Badge } from '@xiltrion/ui/badge';
import { Input } from '@xiltrion/ui/input';
import { Toggle } from '@xiltrion/ui/toggle';
import {
  Filters, createDateFilterField, createFilterQuery, createFilterRule, flattenFilterRules, resolveFilterDate, toFilterDateValue,
  type FilterDateRuleValue, type FilterField, type FilterOperator, type FilterQuery, type FilterRule,
} from '@xiltrion/ui';
import { Tabla, type ColumnaTabla, type OrdenTabla } from '@xiltrion/ui/bloques/tabla';
import type { CaseRow } from '@/api/types';
import { CHANNEL_LABEL, EmptyState, ErrorState, PageHeader, STATUS_LABEL, StatusTag, fmtDate } from '@/components/ui';
import { useCasesInfinite, type CaseListFilters } from './api';
import { ALL_STATUSES, OPEN_STATUSES } from './inboxConfig';
import { useCaseTypes } from './lookups';
import { downloadCsv } from './lib';
import { FavoriteStar, useFavoriteDates } from './FavoriteStar';
import { ImportantIcon } from '@/features/citizens/ImportantMark';

/**
 * Archivo: búsqueda sobre todo el histórico de expedientes, incluidos
 * resueltos, cerrados y archivados. Orientado a encontrar un expediente, no a trabajarlo:
 * para eso está la bandeja de solicitudes. «Mis favoritos» lo reduce a los que la persona marcó con la estrella (en cualquier
 * estado: se guardan como referencia), con la fecha en que los marcó y lo último marcado primero.
 */
const CLOSED_STATUSES = ['TRANSFERRED', 'RESOLVED', 'CLOSED', 'ARCHIVED'];
const SCOPES: Record<string, { label: string; status?: string[] }> = {
  todos: { label: 'Todos los estados' },
  cerrados: { label: 'Cerrados y archivados', status: CLOSED_STATUSES },
  activos: { label: 'Activos', status: OPEN_STATUSES },
};

/** El caso tiene correos al ciudadano que no salieron (la respuesta u otra comunicación): se ve sin abrirlo y lleva a Comunicaciones. */
function FailedEmailsBadge({ c }: { c: CaseRow }) {
  const n = c.failedEmails ?? 0;
  if (!n) return null;
  return (
    <Link to={`/pqrs/${c.id}?tab=comunicaciones`} className="no-underline" onClick={(e) => e.stopPropagation()} title="El ciudadano no recibió esta comunicación: ver en Comunicaciones">
      <Badge variant="destructive" size="sm" className="gap-1"><Icono nombre="mdi:email-alert-outline" aria-hidden /> {n === 1 ? 'Correo no enviado' : `${n} correos no enviados`}</Badge>
    </Link>
  );
}

export function ArchivePage() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') ?? '';
  const scope = sp.get('scope') ?? 'todos';
  const caseType = sp.get('tipo') ?? '';
  const channel = sp.get('canal') ?? '';
  const from = sp.get('desde') ?? '';
  const to = sp.get('hasta') ?? '';
  const favorites = sp.get('favoritos') === '1';
  const sort: 'createdAt' | 'radication' = sp.get('orden') === 'radication' ? 'radication' : 'createdAt';
  const dir: 'asc' | 'desc' = sp.get('dir') === 'asc' ? 'asc' : 'desc';
  const markedAt = useFavoriteDates();
  const [text, setText] = useState(q);
  const [seleccion, setSeleccion] = useState<string[]>([]);
  const caseTypes = useCaseTypes();

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    setSp(next, { replace: true });
  };
  useEffect(() => { setText(q); }, [q]);
  useEffect(() => {
    const t = window.setTimeout(() => { if (text.trim() !== q) set({ q: text.trim() }); }, 350);
    return () => window.clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  const filters: CaseListFilters = useMemo(() => ({
    q: q || undefined,
    status: SCOPES[scope]?.status ?? ALL_STATUSES,
    channel: channel || undefined,
    from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
    favorites: favorites || undefined,
    sort, dir, limit: 100,
  }), [q, scope, channel, from, to, favorites, sort, dir]);
  const query = useCasesInfinite(filters);
  const rows = useMemo(() => {
    const all = query.data?.pages.flatMap((p) => p.items) ?? [];
    return caseType ? all.filter((c) => c.caseTypeCode === caseType) : all;
  }, [query.data, caseType]);

  // El servidor ordena por radicado o fecha (y pagina por cursor); las demás columnas no ordenan. «Mis favoritos» es
  // una lista corta que se ordena en memoria, con lo último marcado primero.
  const columnas: ColumnaTabla<CaseRow>[] = [
    { key: '_fav', titulo: '', valor: () => '', ordenable: false, fija: true, ancho: '2.5rem', render: (c) => <FavoriteStar caseId={c.id} label={c.radication ?? c.subject} /> },
    { key: 'radication', titulo: 'Radicado', valor: (c) => c.radication, render: (c) => <span className="tnum">{c.radication}</span>, secundario: (c) => c.caseTypeName, unaLinea: true, ancho: '9.5rem' },
    { key: 'createdAt', titulo: 'Recibido', valor: (c) => c.createdAt, render: (c) => fmtDate(c.createdAt, false), unaLinea: true },
    { key: 'citizen', titulo: 'Ciudadano', valor: (c) => c.anonymous ? 'Anónimo' : c.citizenName, render: (c) => c.anonymous ? <span className="text-muted-foreground">Anónimo</span> : <span className="inline-flex items-center gap-1">{c.citizenImportant && <ImportantIcon />}{c.citizenName ?? '—'}</span>, ordenable: favorites },
    { key: 'subject', titulo: 'Asunto', valor: (c) => c.subject, lineas: 2, secundario: (c) => c.organizationalUnitName, ordenable: favorites },
    { key: 'channel', titulo: 'Canal', valor: (c) => CHANNEL_LABEL[c.channel] ?? c.channel, unaLinea: true, ordenable: favorites },
    { key: 'status', titulo: 'Estado', valor: (c) => STATUS_LABEL[c.status] ?? c.status, render: (c) => <span className="flex flex-col items-start gap-1"><StatusTag status={c.status} /><FailedEmailsBadge c={c} /></span>, unaLinea: true, ordenable: favorites },
    { key: 'assignee', titulo: 'Último responsable', valor: (c) => c.assigneeName, ordenable: favorites },
    ...(favorites ? [{ key: 'markedAt', titulo: 'Marcado', valor: (c: CaseRow) => markedAt[c.id] ?? '', render: (c: CaseRow) => (markedAt[c.id] ? <span className="tnum">{fmtDate(markedAt[c.id]!, false)}</span> : '—'), ancho: '8rem' }] : []),
  ];
  const orden: OrdenTabla = { key: sort, desc: dir === 'desc' };
  const onOrdenChange = (o: OrdenTabla | null) => set({ orden: o?.key === 'radication' ? 'radication' : '', dir: o?.desc === false ? 'asc' : '' });

  // En el CSV el tipo y el área (y si hay correos sin enviar) (segunda línea de radicado y asunto en pantalla) van como columnas propias.
  const exportables = columnas.flatMap((c) => c.key === 'radication' ? [c, { titulo: 'Tipo', valor: (r: CaseRow) => r.caseTypeName }] : c.key === 'subject' ? [c, { titulo: 'Área', valor: (r: CaseRow) => r.organizationalUnitName }] : c.key === 'status' ? [c, { titulo: 'Correos no enviados', valor: (r: CaseRow) => String(r.failedEmails ?? 0) }] : [c]);
  const exportar = (filas: CaseRow[]) => downloadCsv(`archivo-${new Date().toISOString().slice(0, 10)}.csv`, exportables.map((c) => c.titulo), filas.map((r) => exportables.map((c) => c.valor(r))));

  return (
    <div>
      <PageHeader title="Archivo de expedientes" subtitle="Busca en todo el histórico, incluidos los cerrados y archivados, o en los que marcaste como favoritos. Para trabajar los casos activos usa la bandeja de solicitudes." />
      <div className="bg-card border border-border rounded-lg p-3 mb-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Icono nombre='mdi:magnify' className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" aria-hidden />
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Radicado, ciudadano, identificación, asunto o contenido…" className="pl-9 h-10" aria-label="Buscar en el archivo" autoFocus />
          </div>
          <Toggle variant="outline" className="h-10" pressed={favorites} onPressedChange={(on) => set({ favoritos: on ? '1' : '' })} aria-label="Solo mis favoritos">
            <Icono nombre={favorites ? 'bi:star-fill' : 'bi:star'} className={favorites ? 'text-favorito' : undefined} /> Mis favoritos
          </Toggle>
        </div>
        <ArchiveFilterBar sp={sp} onChange={set} caseTypes={(caseTypes.data ?? []).map((t) => ({ value: t.code, label: t.name }))} />
      </div>

      {query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : (
        <Tabla<CaseRow>
          columnas={columnas}
          filas={rows}
          claveFila={(c) => c.id}
          onClicFila={(c) => nav(`/pqrs/${c.id}`)}
          cargando={query.isLoading}
          densa
          key={favorites ? 'fav' : 'all'}
          {...(favorites ? { ordenInicial: { key: 'markedAt', desc: true } } : { orden, onOrdenChange })}
          masFilas={{ hayMas: !!query.hasNextPage, cargando: query.isFetchingNextPage, onCargar: () => void query.fetchNextPage() }}
          virtual={{ alto: 'calc(100vh - 24rem)' }}
          seleccion={seleccion}
          onSeleccionChange={setSeleccion}
          barra={({ visibles, seleccionadas }) => (
            <Button variant="outline" size="sm" onClick={() => exportar(seleccionadas.length ? seleccionadas : visibles)} disabled={!visibles.length}>
              <Icono nombre='mdi:download' /> {seleccionadas.length ? `Exportar ${seleccionadas.length} seleccionados` : 'Exportar CSV'}
            </Button>
          )}
          vacio={favorites && !q ? <EmptyState icon="bi:star" title="Aún no tienes favoritos con estos filtros." hint="Marca con la estrella cualquier radicado (aquí, en la bandeja o en su detalle) y lo encontrarás con «Mis favoritos», aunque ya esté cerrado. Solo tú los ves." /> : <EmptyState icon="mdi:archive-search-outline" title={q ? `Sin resultados para «${q}»` : 'El archivo está vacío'} hint={q ? 'Prueba con el número de radicado completo, el nombre o la identificación del ciudadano.' : 'Cuando se radiquen expedientes aparecerán aquí, incluidos los cerrados.'} />}
        />
      )}
    </div>
  );
}

const IS: FilterOperator[] = [{ value: 'is', label: 'es', arity: 'one' }];
const ARCHIVE_KEYS = ['scope', 'tipo', 'canal', 'desde', 'hasta'] as const;
type ArchiveParams = Partial<Record<(typeof ARCHIVE_KEYS)[number], string>>;

const first = (v: unknown): string | undefined => (Array.isArray(v) ? (v[0] as string | undefined) : typeof v === 'string' ? v : undefined);
const iso = (d: Date | null | undefined) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined);
const fromIso = (x: string) => toFilterDateValue(new Date(`${x}T00:00:00`));

function paramsToQuery(p: ArchiveParams): FilterQuery {
  const rules: FilterRule[] = [];
  const rule = (id: string, operator: string, value?: unknown) => rules.push(createFilterRule({ id: `r-${id}`, path: [id], operator, value }));
  if (p.scope && p.scope !== 'todos') rule('estado', 'is', [p.scope]);
  if (p.tipo) rule('tipo', 'is', [p.tipo]);
  if (p.canal) rule('canal', 'is', [p.canal]);
  if (p.desde && p.hasta) rule('radicacion', p.desde === p.hasta ? 'is' : 'between', p.desde === p.hasta ? fromIso(p.desde) : [fromIso(p.desde), fromIso(p.hasta)]);
  else if (p.desde) rule('radicacion', 'is_on_or_after', fromIso(p.desde));
  else if (p.hasta) rule('radicacion', 'is_on_or_before', fromIso(p.hasta));
  return createFilterQuery(rules);
}

function queryToParams(q: FilterQuery): ArchiveParams {
  const p: ArchiveParams = {};
  for (const r of flattenFilterRules(q)) {
    if (!r.operator) continue;
    const field = r.path[0];
    if (field === 'estado') p.scope = first(r.value);
    else if (field === 'tipo') p.tipo = first(r.value);
    else if (field === 'canal') p.canal = first(r.value);
    else if (field === 'radicacion') {
      const v = r.value as FilterDateRuleValue | undefined;
      if (!v) continue;
      if (Array.isArray(v)) { p.desde = iso(resolveFilterDate(v[0])); p.hasta = iso(resolveFilterDate(v[1])); }
      else {
        const d = iso(resolveFilterDate(v));
        if (r.operator === 'is') { p.desde = d; p.hasta = d; }
        else if (r.operator === 'is_on_or_after') p.desde = d;
        else if (r.operator === 'is_on_or_before') p.hasta = d;
      }
    }
  }
  return p;
}

const sameParams = (a: ArchiveParams, b: ArchiveParams) => ARCHIVE_KEYS.every((k) => (a[k] || '') === (b[k] || ''));

/** Filtros del archivo como barra de chips; la URL guarda solo lo que filtra. */
function ArchiveFilterBar({ sp, onChange, caseTypes }: { sp: URLSearchParams; onChange: (patch: Record<string, string>) => void; caseTypes: Array<{ value: string; label: string }> }) {
  const current = useMemo<ArchiveParams>(() => Object.fromEntries(ARCHIVE_KEYS.map((k) => [k, sp.get(k) ?? ''])), [sp]);
  const [query, setQuery] = useState<FilterQuery>(() => paramsToQuery(current));
  useEffect(() => { setQuery((cur) => (sameParams(queryToParams(cur), current) ? cur : paramsToQuery(current))); }, [current]);
  const fields = useMemo<FilterField[]>(() => [
    { id: 'estado', label: 'Estado', type: 'select', operators: IS, searchable: false, icon: <Icono nombre="mdi:record-circle-outline" />, options: Object.entries(SCOPES).filter(([k]) => k !== 'todos').map(([k, v]) => ({ value: k, label: v.label })) },
    { id: 'tipo', label: 'Tipo', type: 'select', operators: IS, searchable: false, icon: <Icono nombre="mdi:shape-outline" />, options: caseTypes },
    { id: 'canal', label: 'Canal', type: 'select', operators: IS, searchable: false, icon: <Icono nombre="mdi:email-outline" />, options: Object.entries(CHANNEL_LABEL).map(([v, label]) => ({ value: v, label })) },
    createDateFilterField('radicacion', 'Fecha de radicación', { operators: ['between', 'is', 'is_on_or_after', 'is_on_or_before'], icon: <Icono nombre="mdi:calendar-range-outline" /> }),
  ], [caseTypes]);
  return (
    <Filters
      fields={fields}
      query={query}
      showClear
      size="sm"
      onQueryChange={(q) => {
        setQuery(q);
        const next = queryToParams(q);
        if (!sameParams(next, current)) onChange(Object.fromEntries(ARCHIVE_KEYS.map((k) => [k, next[k] ?? ''])));
      }}
    />
  );
}
